import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  tagline: string;
  image: string;
}

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onConsult: () => void;
}

export default function ProductDetailModal({ product, onClose, onConsult }: ProductDetailModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative animate-scale-in"
        style={{ animation: 'scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
      >
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center transition-colors shadow-md"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* 顶部图片 */}
        <div className="aspect-[16/9] rounded-t-2xl overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>

        {/* 内容 */}
        <div className="p-6 md:p-8">
          <h2 className="text-2xl font-bold" style={{ color: '#1A1A2E' }}>{product.name}</h2>
          <p className="mt-2 font-medium" style={{ color: '#FF6B3D' }}>{product.tagline}</p>

          <div className="border-t border-gray-200 my-6" />

          {/* 预留区域 */}
          <PlaceholderSection title="产品简介" />
          <PlaceholderSection title="保障范围" />
          <PlaceholderSection title="投保须知" />

          {/* CTA */}
          <button
            onClick={onConsult}
            className="mt-8 w-full py-3 rounded-lg text-white font-semibold transition-all duration-300 hover:shadow-lg"
            style={{ backgroundColor: '#FF6B3D' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#E55A2B'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#FF6B3D'; }}
          >
            立即咨询
          </button>
        </div>
      </div>
    </div>
  );
}

function PlaceholderSection({ title }: { title: string }) {
  return (
    <div className="rounded-lg p-6 mt-4" style={{ backgroundColor: '#F7F8FA' }}>
      <h4 className="font-semibold text-sm" style={{ color: '#9CA3AF' }}>{title}（内容待填充）</h4>
      <div className="mt-3 space-y-2">
        <div className="h-3 rounded" style={{ backgroundColor: '#E5E7EB', width: '100%' }} />
        <div className="h-3 rounded" style={{ backgroundColor: '#E5E7EB', width: '85%' }} />
        <div className="h-3 rounded" style={{ backgroundColor: '#E5E7EB', width: '70%' }} />
      </div>
    </div>
  );
}
